import React from 'react';
import { formatCompactNumber } from '../format';

/**
 * Page controls for a table.
 *
 * Shows the range and the total ("51–100 of 1,284") rather than only page
 * numbers, because the first question an operator has about an ops table is
 * how big the thing they are looking at actually is — "page 2 of 26" answers
 * that only after arithmetic.
 *
 * Renders nothing for a single page. A pager under a nine-row table is noise
 * that makes the page look busier than the data is.
 */
const btn = 'rounded-md border border-hairline px-2 py-1 text-[11px] font-medium text-ink-secondary hover:bg-surface-page hover:text-ink disabled:cursor-default disabled:opacity-35 disabled:hover:bg-transparent';

const PAGE_SIZES = [25, 50, 100, 200];

export default function Pagination({ meta, onPage, onPageSize, loading = false }) {
  if (!meta) return null;

  const { page = 1, page_size: pageSize = 50, total = 0, total_pages: totalPages = 1 } = meta;
  if (total === 0) return null;

  const first = (page - 1) * pageSize + 1;
  const last = Math.min(page * pageSize, total);
  const single = totalPages <= 1;

  return (
    <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
      <p className="text-[11px] tabular-nums text-ink-muted">
        {single
          ? <>{formatCompactNumber(total)} {total === 1 ? 'row' : 'rows'}</>
          : <>{formatCompactNumber(first)}–{formatCompactNumber(last)} of {formatCompactNumber(total)}</>}
      </p>

      <div className="flex items-center gap-3">
        {onPageSize && (
          <label className="flex items-center gap-1.5 text-[11px] text-ink-muted">
            Rows
            <select
              value={pageSize}
              onChange={(e) => onPageSize(Number(e.target.value))}
              className="rounded-md border border-hairline bg-surface px-1.5 py-1 text-[11px] text-ink focus:outline-none focus:ring-1 focus:ring-raspberry-300"
            >
              {PAGE_SIZES.map((n) => <option key={n} value={n}>{n}</option>)}
            </select>
          </label>
        )}

        {!single && (
          <div className="flex items-center gap-1">
            <button type="button" className={btn} onClick={() => onPage(1)} disabled={loading || page <= 1} aria-label="First page">«</button>
            <button type="button" className={btn} onClick={() => onPage(page - 1)} disabled={loading || page <= 1}>Prev</button>
            <span className="px-1.5 text-[11px] tabular-nums text-ink-secondary">
              {page} / {totalPages}
            </span>
            <button type="button" className={btn} onClick={() => onPage(page + 1)} disabled={loading || page >= totalPages}>Next</button>
            <button type="button" className={btn} onClick={() => onPage(totalPages)} disabled={loading || page >= totalPages} aria-label="Last page">»</button>
          </div>
        )}
      </div>
    </div>
  );
}
